import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useBrandContext } from "../../context/BrandContext";
import { useToast } from "../../context/ToastModalContext";
import usePaymentProvider, {
  PaymentTokenResult,
} from "../../payments/PaymentProvider";

import Button from "../../components/button/Button";
import ButtonSecondary from "../../components/buttonSecondary/ButtonSecondary";
import Input from "../../components/input/Input";
import ToggleSwitch from "../../components/toggleSwitch/ToggleSwitch";

import {
  deleteUserPaymentMethod,
  getUserPaymentMethodById,
  getUserPaymentMethods,
  postIuguPaymentTokenResponse,
  postUserPaymentMethod,
  createStripeSetupIntent,
  setDefaultCreditCard,
} from "../../services/userPaymentService";

import checkCircle from "../../assets/check_circle_outline.svg";
import deleteOutline from "../../assets/delete_outline.svg";
import { ReactComponent as LoaderSecondary } from "../../assets/SpinnerBlack.svg";
import { ICreditCard } from "../../shared/models/creditCard/creditCard";
import { getAddCardErrorMessage } from "../../utils/paymentErrors";
import { validateCNPJ } from "../../utils/validateCNPJ";
import { validateCPF } from "../../utils/validateCPF";

import "./AddCreditCard.css";
import { useHeaderContext } from "../../context/HeaderContext";
import { getUser } from "../../services/userService";
import { hasBillingAddress } from "../../utils/billingAddress";

/**
 * BIN do cartao com ate 8 digitos, que e o que o backend grava em upm_bin e usa
 * para casar a faixa de BIN (gateway, arte e cupom). Com 6, as faixas de 8
 * digitos nunca casariam. Vai no cadastro junto com o token para o BIN ficar
 * salvo mesmo quando o gateway nao devolve os primeiros digitos.
 */
const getCardBin = (cardNumber: string): string | undefined => {
  const digits = (cardNumber || "").replace(/\D/g, "");

  return digits.length >= 6 ? digits.slice(0, 8) : undefined;
};

const AddCreditCard: React.FC = () => {
  const paymentProvider = usePaymentProvider({
    stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
      ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
      : "",
    iuguAccountId: process.env.REACT_APP_TOKEN_IUGU_ACCOUNT_ID
      ? process.env.REACT_APP_TOKEN_IUGU_ACCOUNT_ID
      : "",
    iuguTestMode: process.env.REACT_APP_IUGU_PAYMENT_TEST
      ? process.env.REACT_APP_IUGU_PAYMENT_TEST
      : "false",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCardInfo, setIsLoadingCardInfo] = useState(false);
  const [isErrorCardInfo, setIsErrorCardInfo] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { currenCreditCardId } = useParams();
  type Inputs = {
    holderName: string;
    cardNumber: string;
    cardExpiredDate: string;
    codeCVV: string;
    toggleSwitch: boolean;
    cpfCnpj: string;
  };
  const [form, setForm] = useState<Inputs>({
    holderName: "",
    cardNumber: "",
    cardExpiredDate: "",
    codeCVV: "",
    toggleSwitch: false,
    cpfCnpj: "",
  });
  type Touched = Partial<Record<keyof Inputs, boolean>>;
  const [touched, setTouched] = useState<Touched>({});
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState("");
  const [isFirstCreditCard, setIsFirstCreditCard] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [creditCardInfo, setCreditCardInfo] = useState<ICreditCard>();
  const { showToast } = useToast();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const [cardholderCPFCNPJ, setCardholderCPFCNPJ] = useState<string>("");
  // Cadastrar cartao exige endereco de cobranca no perfil, como o credito exige
  // CPF: sem ele a Pagar.me recusa toda cobranca, e pedir antes evita o usuario
  // descobrir pela recusa.
  const [isAddressMissing, setIsAddressMissing] = useState(false);
  // const routePaths = location.pathname.split('/');

  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};
    if (!newInputs.holderName.trim()) {
      newErrors.holderName = "Campo Obrigatório";
    }

    if (newInputs.cpfCnpj.trim().length < 15) {
      if (!validateCPF(newInputs.cpfCnpj.trim())) {
        newErrors.cpfCnpj = "Campo Inválido";
      }
    } else {
      if (!validateCNPJ(newInputs.cpfCnpj.trim())) {
        newErrors.cpfCnpj = "Campo Inválido";
      }
    }

    if (!newInputs.cardExpiredDate.trim()) {
      newErrors.cardExpiredDate = "Campo Obrigatório";
    }

    if (!newInputs.codeCVV.trim()) {
      newErrors.codeCVV = "Campo Obrigatório";
    }

    if (!form.cardNumber.trim()) {
      newErrors.cardNumber = "Campo Obrigatório";
    }

    setErrorsValidate(newErrors);
    return newErrors;
  };

  const getPaymentMethodById = async (id: any) => {
    try {
      setIsLoadingCardInfo(true);
      const response = await getUserPaymentMethodById(id);
      if (response.id) {
        setForm({
          ...form,
          cardNumber: response?.data?.display_number,
          holderName: response?.data?.holder_name || "",
          cardExpiredDate: `${response?.data?.month}/${String(
            response?.data?.year
          ).substring(2, 4)}`.padStart(4, "0"),
          codeCVV: "XXX",
          toggleSwitch: response?.is_default,
        });
        setIsDefault(response?.is_default);
        setCreditCardInfo(response);
      }
      setIsLoadingCardInfo(false);
      setIsErrorCardInfo(false);
    } catch (er) {
      setIsLoadingCardInfo(false);
      setIsErrorCardInfo(true);
      showToast({
        title: "Falha",
        messageModal:
          "Falha ao carregar informações do cartão. Por favor, tente novamente",
        textEnterButton: "Tentar novamente",
        allowButtonClosed: true,
        handleEnterButton: () => getPaymentMethodById(id),
        closeButtonText: "Fechar",
      });
      console.log(er);
    }
  };

  useEffect(() => {
    const getPaymentMenthods = async () => {
      try {
        const response = await getUserPaymentMethods();
        if (response.length === 0) {
          setIsFirstCreditCard(true);
          setForm({ ...form, toggleSwitch: true });
        } else {
          setIsFirstCreditCard(false);
        }
        setErrorFetch("");
      } catch (error: any) {
        if (error?.message === "Network Error") {
          setErrorFetch(
            "Sua conexão está instável. Por favor, tente mais tarde."
          );
        } else {
          setErrorFetch("Falha no servidor. Por favor tente novamente.");
        }
      }
    };
    getPaymentMenthods();
  }, []);

  useEffect(() => {
    if (currenCreditCardId !== undefined) {
      getPaymentMethodById(currenCreditCardId);
    }
  }, []);

  // So no cadastro de cartao novo; abrir um cartao salvo (definir como
  // principal, excluir) segue liberado.
  useEffect(() => {
    if (currenCreditCardId !== undefined) return;

    let isMounted = true;

    (async () => {
      let user: any;
      try {
        // Busca de novo, e nao do contexto: o usuario pode estar voltando do
        // perfil com o endereco recem-salvo.
        user = await getUser();
      } catch {
        // Sem o perfil nao da para decidir; o backend ainda barra a cobranca.
        return;
      }
      if (!isMounted) return;

      if (hasBillingAddress(user)) return;

      setIsAddressMissing(true);
      showToast({
        title: "Endereço necessário",
        messageModal:
          "Para cadastrar um cartão, precisamos do seu endereço de cobrança. Preencha no seu perfil e você volta para cá.",
        allowButtonClosed: true,
        closeButtonText: "OK",
      });
      navigate(`/${brandContext.brandName}/userProfile`, {
        state: {
          addressRequired: true,
          returnTo: `/${brandContext.brandName}/addCreditCard`,
        },
        replace: true,
      });
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const saveIuguCard = async (
    paymentToken: PaymentTokenResult,
    cardholderName: string
  ) => {
    if (paymentToken.raw?.errors) {
      throw new Error("Erro ao cadastrar o cartão.");
    }

    const iuguTokenResponse = await postIuguPaymentTokenResponse(
      paymentToken.raw
    );
    const iuguResponse = iuguTokenResponse || paymentToken.raw;

    if ((iuguResponse as any)?.errors) {
      throw new Error("Erro ao cadastrar o cartão.");
    }

    const iuguTokenId = iuguTokenResponse?.id || paymentToken.raw?.id;

    if (!iuguTokenId) {
      throw new Error(
        "Não foi possível gerar o token do cartão. Por favor, tente novamente."
      );
    }

    const brand = iuguResponse?.extra_info?.brand || "Cartão";
    const last4 =
      iuguResponse?.extra_info?.display_number?.replace(/\D/g, "")?.slice(-4) ||
      form.cardNumber.replace(/\D/g, "").slice(-4) ||
      "****";

    const bin = getCardBin(form.cardNumber);

    const userPaymentMethodsPost = {
      description: `${brand} * ${last4}`,
      token: iuguTokenId,
      set_as_default: isFirstCreditCard ? true : form.toggleSwitch,
      cpf_cnpj: cardholderCPFCNPJ,
      bin: bin,
      // A IUGU costuma devolver o nome, mas o backend so usa este valor como
      // fallback quando o gateway nao informou.
      holder_name: cardholderName,
      // Fixa o gateway que tokenizou. Ausente, o backend re-resolve pelo bin
      // e pode divergir do que foi usado aqui.
      ...(paymentToken.pgwId ? { pgw_id: paymentToken.pgwId } : {}),
    };

    const resultAddUserPaymentMethods = await postUserPaymentMethod(
      userPaymentMethodsPost
    );

    if (resultAddUserPaymentMethods) {
      backNavigation();
    }
  };

  const savePagarmeCard = async (
    paymentToken: PaymentTokenResult,
    cardholderName: string
  ) => {
    // O token_... da Pagar.me vale por poucos segundos: o backend troca por um
    // card_... no cliente da conta, com o endereco de cobranca do perfil.
    const pagarmeTokenId = paymentToken.raw?.id;

    if (!pagarmeTokenId) {
      throw new Error(
        "Não foi possível gerar o token do cartão. Por favor, tente novamente."
      );
    }

    const brand = paymentToken.raw?.card?.brand || "Cartão";
    const last4 =
      paymentToken.raw?.card?.last_four_digits ||
      form.cardNumber.replace(/\D/g, "").slice(-4) ||
      "****";

    const bin = getCardBin(form.cardNumber);

    const userPaymentMethodsPost = {
      description: `${brand} * ${last4}`,
      token: pagarmeTokenId,
      set_as_default: isFirstCreditCard ? true : form.toggleSwitch,
      cpf_cnpj: cardholderCPFCNPJ,
      bin: bin,
      holder_name: cardholderName,
      // Fixa o gateway que tokenizou. Ausente, o backend re-resolve pelo bin
      // e pode divergir do que foi usado aqui.
      ...(paymentToken.pgwId ? { pgw_id: paymentToken.pgwId } : {}),
    };

    const resultAddUserPaymentMethods = await postUserPaymentMethod(
      userPaymentMethodsPost
    );

    if (resultAddUserPaymentMethods) {
      backNavigation();
    }
  };

  const saveStripeCardWithSetupIntent = async (
    stripeToken: string,
    cardholderName: string,
    // Mesma chave usada para tokenizar. Se vier da API, acompanha a conta e o
    // modo do backend que cria o SetupIntent.
    publishableKey?: string,
    pgwId?: string
  ) => {
    const setupIntentResponse = await createStripeSetupIntent();

    if (!setupIntentResponse?.clientSecret) {
      throw new Error(
        "Não foi possível iniciar o cadastro do cartão. Por favor, tente novamente."
      );
    }

    // Cria PaymentMethod a partir do token
    const paymentMethod = await paymentProvider.createPaymentMethodFromToken(
      stripeToken,
      cardholderName,
      publishableKey
    );

    if (!paymentMethod?.id) {
      throw new Error(
        "Não foi possível criar o método de pagamento. Por favor, tente novamente."
      );
    }

    const { setupIntent, error } = await paymentProvider.confirmStripeCardSetup(
      setupIntentResponse.clientSecret,
      paymentMethod.id,
      publishableKey
    );

    if (error) {
      throw new Error(error?.message || "Falha ao confirmar o cartão.");
    }

    if (!setupIntent || setupIntent.status !== "succeeded") {
      throw new Error(
        "Não foi possível salvar o cartão. Por favor, tente novamente."
      );
    }

    const paymentMethodId =
      typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

    if (!paymentMethodId) {
      throw new Error(
        "Não foi possível identificar o cartão salvo. Por favor, tente novamente."
      );
    }

    const stripeBrand =
      (setupIntent as any)?.payment_method?.card?.brand ||
      (setupIntent as any)?.latest_attempt?.payment_method_details?.card
        ?.brand ||
      "Cartão";

    const stripeLast4 =
      (setupIntent as any)?.payment_method?.card?.last4 ||
      (setupIntent as any)?.latest_attempt?.payment_method_details?.card
        ?.last4 ||
      form.cardNumber.replace(/\D/g, "").slice(-4) ||
      "****";

    const bin = getCardBin(form.cardNumber);

    const userPaymentMethodsPost = {
      description: `${stripeBrand} * ${stripeLast4}`,
      token: paymentMethodId, // Stripe: aqui o "token" é o paymentMethodId
      set_as_default: isFirstCreditCard ? true : form.toggleSwitch,
      cpf_cnpj: cardholderCPFCNPJ,
      bin: bin,
      // Fallback: a Stripe costuma não devolver o nome do titular. O backend
      // só usa este valor quando o gateway não informou o nome.
      holder_name: cardholderName,
      // Fixa o gateway que tokenizou. Ausente, o backend re-resolve pelo bin
      // e pode divergir do que foi usado aqui.
      ...(pgwId ? { pgw_id: pgwId } : {}),
    };

    const resultAddUserPaymentMethods = await postUserPaymentMethod(
      userPaymentMethodsPost
    );

    if (!resultAddUserPaymentMethods) {
      throw new Error("Falha ao salvar o cartão no servidor. Tente novamente.");
    }

    if (!isFirstCreditCard && form.toggleSwitch) {
      const response = await setDefaultCreditCard(paymentMethodId);

      if (response?.status !== 204) {
        throw new Error(
          "Cartão salvo, mas não foi possível defini-lo como principal."
        );
      }
    }

    backNavigation();
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (isAddressMissing && !currenCreditCardId) return;
    const errors = validate(form);
    if (Object.keys(errors).length !== 0) return;

    setErrorFetch("");
    setIsLoading(true);

    try {
      if (currenCreditCardId && form.toggleSwitch && !isDefault) {
        await updateCreditCard();
        return;
      }

      // Um campo so de titular, como esta no cartao. A IUGU ainda pede nome e
      // sobrenome separados: o primeiro termo vira first_name e o resto last_name.
      const holderName = form.holderName.trim().replace(/\s+/g, " ");
      const [holderFirstName, ...holderRest] = holderName.split(" ");
      const holderLastName = holderRest.join(" ");

      const payloadCard = {
        number: form.cardNumber.split(" ").join(""),
        verification_value: form.codeCVV,
        first_name: holderFirstName,
        last_name: holderLastName,
        month: form.cardExpiredDate.split("/")[0],
        year: form.cardExpiredDate.split("/")[1],
      };

      const paymentToken = await paymentProvider.createPaymentToken(
        payloadCard
      );

      const cardholderName = holderName;

      if (paymentToken.provider === "iugu") {
        await saveIuguCard(paymentToken, cardholderName);
        return;
      }

      if (paymentToken.provider === "pagarme") {
        await savePagarmeCard(paymentToken, cardholderName);
        return;
      }

      if (!paymentToken?.raw?.id) {
        setErrorFetch(
          "Não foi possível gerar o token do cartão. Por favor, tente novamente."
        );
        return;
      }

      // Para Stripe, usa o token no fluxo de SetupIntent
      await saveStripeCardWithSetupIntent(
        paymentToken.raw.id,
        cardholderName,
        paymentToken.publishableKey,
        paymentToken.pgwId
      );
    } catch (error: any) {
      setErrorFetch(getAddCardErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateCreditCard = async () => {
    try {
      const response = await setDefaultCreditCard(creditCardInfo?.id);
      setIsLoading(false);
      if (response.status === 204) {
        backNavigation();
      } else {
        setErrorFetch(
          "Não foi possível definitir seu cartão como default. Por favor, tente mais tarde."
        );
      }
    } catch (error: any) {
      setIsLoading(false);
      if (error?.message === "Network Error") {
        setErrorFetch(
          "Sua conexão está instável. Por favor, tente mais tarde."
        );
      } else {
        setErrorFetch("Falha no servidor. Por favor tente novamente.");
      }
    }
  };

  const backNavigation = () => {
    navigate(`/${brandContext.brandName}/paymentMethods`);
  };

  const handleDeleteCreditCard = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await deleteUserPaymentMethod(currenCreditCardId!);
      setIsDeleteLoading(false);
      if (response === 200) {
        backNavigation();
        return;
      } else {
        setErrorFetch("Erro ao deletar cartão.");
      }
    } catch (error: any) {
      setIsDeleteLoading(false);
      if (error?.message === "Network Error") {
        setErrorFetch(
          "Sua conexão está instável. Por favor, tente mais tarde."
        );
      } else {
        setErrorFetch("Falha no servidor. Por favor tente novamente.");
      }
    }
  };

  const handleCardExpiredDateChange = (event: any) => {
    let dateInputValue = event.target.value.replace(/\D/g, "");
    if (dateInputValue.length > 2) {
      dateInputValue =
        dateInputValue.slice(0, 2) + "/" + dateInputValue.slice(2);
    }
    setForm({ ...form, cardExpiredDate: dateInputValue });
  };

  const handleCodeCVVChange = (event: any) => {
    let codeInputValue = event.target.value.replace(/\D/g, "");

    // 4 digitos: o Amex usa CID de 4 (as demais bandeiras, 3).
    if (codeInputValue.length > 4) {
      codeInputValue = codeInputValue.substring(0, 4);
    }
    setForm({ ...form, codeCVV: codeInputValue });
  };

  const handleCardCPFCPNJChange = (event: any) => {
    const inputValue = event.target.value.replace(/\D/g, "");

    setErrorsValidate({ ...errorsValidate, cpfCnpj: "" });

    if (inputValue.length < 12) {
      setCardholderCPFCNPJ(
        inputValue
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
      );
    } else {
      setCardholderCPFCNPJ(
        inputValue
          .replace(/(\d{2})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1/$2")
          .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
      );
    }
  };

  useEffect(() => {
    setForm({ ...form, cpfCnpj: cardholderCPFCNPJ });
  }, [cardholderCPFCNPJ]);

  const handleCardNumberChange = (event: any) => {
    let cardNumberInputValue = event.target.value.replace(/\D/g, "");

    cardNumberInputValue = cardNumberInputValue.replace(
      /(\d{4})(?=\d)/g,
      "$1 "
    );

    if (cardNumberInputValue.length > 19) {
      cardNumberInputValue = cardNumberInputValue.substring(0, 19);
    }
    setForm({ ...form, cardNumber: cardNumberInputValue });
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: "Cartão de crédito",
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig]);

  return (
    <>
      {!isLoadingCardInfo && !isErrorCardInfo ? (
        <div className="add-credit-content-container">
          <p className="add-credit-content-text">
            Insira os dados do cartão de crédito
          </p>
          <form onSubmit={handleSubmit}>
            <div className="add-credit-form-inputs">
              <Input
                labelName="Nome do titular"
                htmlFor="holderName"
                type="text"
                id="holderName"
                className={
                  errorsValidate.holderName
                    ? "input-pattern error-input"
                    : "input-pattern"
                }
                placeholder="Nome como está impresso no cartão"
                value={form.holderName}
                onChange={(event) => {
                  setForm({ ...form, holderName: event.target.value });
                }}
                onBlur={() => setTouched({ ...touched, holderName: true })}
                isErrorValidate={errorsValidate.holderName ? true : false}
                messageError={errorsValidate.holderName}
                disabled={currenCreditCardId ? true : false}
              />
              {!currenCreditCardId && (
                <Input
                  labelName="CPF/CNPJ"
                  htmlFor="cpfCnpj"
                  type="text"
                  id="cpfCnpj"
                  className={
                    errorsValidate.cpfCnpj
                      ? "input-pattern error-input"
                      : "input-pattern"
                  }
                  placeholder="Insira o CPF ou CNPJ do titular do cartão"
                  value={cardholderCPFCNPJ}
                  onChange={handleCardCPFCPNJChange}
                  onBlur={() => setTouched({ ...touched, cpfCnpj: true })}
                  isErrorValidate={errorsValidate.cpfCnpj ? true : false}
                  messageError={errorsValidate.cpfCnpj}
                  disabled={currenCreditCardId ? true : false}
                  maxLength={18}
                />
              )}
              <Input
                labelName="Número do cartão"
                htmlFor="cardNumber"
                type="text"
                id="cardNumber"
                className={
                  errorsValidate.cardNumber
                    ? "input-pattern error-input"
                    : "input-pattern"
                }
                placeholder="Insira o número do cartão"
                value={form.cardNumber}
                maxLength={19}
                onChange={handleCardNumberChange}
                onBlur={() => setTouched({ ...touched, cardNumber: true })}
                isErrorValidate={errorsValidate.cardNumber ? true : false}
                messageError={errorsValidate.cardNumber}
                disabled={currenCreditCardId ? true : false}
              />

              <div className="add-credit-card-info-card">
                <Input
                  labelName="Data de Vencimento"
                  htmlFor="cardExpiredDate"
                  type="text"
                  id="cardExpiredDate"
                  style={{ width: "calc(100% - 22px)" }}
                  placeholder="MM/AA"
                  className={
                    errorsValidate.cardExpiredDate
                      ? "input-pattern error-input"
                      : "input-pattern"
                  }
                  value={form.cardExpiredDate}
                  onChange={handleCardExpiredDateChange}
                  onBlur={() =>
                    setTouched({ ...touched, cardExpiredDate: true })
                  }
                  isErrorValidate={
                    errorsValidate.cardExpiredDate ? true : false
                  }
                  messageError={errorsValidate.cardExpiredDate}
                  maxLength={5}
                  disabled={currenCreditCardId ? true : false}
                />
                <Input
                  labelName="CVV"
                  htmlFor="codeCVV"
                  type="text"
                  id="codeCVV"
                  style={{ width: "calc(100% - 22px)" }}
                  className={
                    errorsValidate.codeCVV
                      ? "input-pattern error-input"
                      : "input-pattern"
                  }
                  placeholder="123"
                  value={form.codeCVV}
                  maxLength={4}
                  onChange={handleCodeCVVChange}
                  onBlur={() => setTouched({ ...touched, codeCVV: true })}
                  isErrorValidate={errorsValidate.codeCVV ? true : false}
                  messageError={errorsValidate.codeCVV}
                  disabled={currenCreditCardId ? true : false}
                />
              </div>
              <ToggleSwitch
                labelText="Escolha como cartão principal"
                disabled={isDefault || isFirstCreditCard}
                checked={form.toggleSwitch}
                onChange={(e) =>
                  setForm({ ...form, toggleSwitch: e.target.checked })
                }
              />
              <b className="error-fetch-register">
                {" "}
                {errorFetch ? errorFetch : null}
              </b>
            </div>
            {!isDefault && (
              <Button
                type="submit"
                children="Salvar Cartão"
                loading={isLoading}
                disabled={isLoading}
                style={{
                  marginBottom: "19px",
                  color: "var(--Neutral-Regular, #787E7D)",
                  backgroundColor: "#fff",
                  border: "1px solid #787E7D",
                }}
                spinnerSecondary={true}
                image={
                  <img
                    width="20px"
                    height="20px"
                    alt="add-button-icon"
                    src={checkCircle}
                  />
                }
              />
            )}
            {currenCreditCardId && !isDefault && (
              <ButtonSecondary
                children="Excluir cartão"
                image={<img alt="delete-icon" src={deleteOutline} />}
                disabled={isLoading || isDeleteLoading}
                onClick={handleDeleteCreditCard}
                loading={isDeleteLoading}
              />
            )}
          </form>
        </div>
      ) : (
        !isErrorCardInfo && <LoaderSecondary />
      )}
    </>
  );
};

export default AddCreditCard;
